import {
	convertLocalToUtcAccordingToTimezone,
	convertUtcToLocalAccordingToTimezone,
	getDiff,
	humanizeDuration,
	toISOString,
} from "@sembark-travel/datetime-utils"
import { TChildrenArray } from "../Tourists"
import { IFlightServicesParams, IPreviewFlightData } from "./NewCalculatePrice"
import { TWorldAirline, TWorldAirport } from "../World"
import {
	useCurrencyPairRatioFieldValue,
	useFieldValue,
} from "@sembark-travel/ui-form"
import {
	addMoney,
	allocateMoneyToRatio,
	createMoney,
	divideMoneyBy,
	formatMoneyByDecimal,
	getMoneyRatio,
	makeCurrencyConverter,
	moneyParseByDecimal,
	multipleMoneyBy,
} from "@sembark-travel/money"
import { useMemo } from "react"
import {
	TPricePerPersonAllocation,
	TTaxAppliedOn,
	getFlightComponentPriceCalculationValues,
	TGivenQuotePricingStrategy,
} from "../Trips"
import pluralize from "pluralize"

export type TFlightClass =
	| "Business"
	| "Economy"
	| "First Class"
	| "Premium Economy"
export type TFlightFormFillMode = "AUTO" | "MANUAL"
export type TFlightType = "ROUND_TRIP" | "ONE_WAY" | "MULTI_CITY"
export type TFlightPaxType = "adult" | "child" | "infant"
export type TFlight = NonNullable<IFlightServicesParams["flights"]>[number]
export const DEFAULT_FLIGHT_CLASS: TFlightClass = "Economy"
export const ADDITIONAL_FLIGHT_OPTIONS = [
	{
		id: 2,
		name: "Baggage",
		value: "baggage",
	},
	{
		id: 3,
		name: "Fare Type",
		value: "fare",
	},
	{
		id: 4,
		name: "Meals",
		value: "meal",
	},
	{
		id: 5,
		name: "Notes for Customer",
		value: "notes_customer",
	},
]
export const FLIGHT_CATEGORY_CLASSES: Array<TFlightClass> = [
	"Economy",
	"Premium Economy",
	"Business",
	"First Class",
]

export type TFlightPerPersonPrice = {
	adult?: number
	child?: number
	infant?: number
}
export interface IFlightPricing {
	currency: string
	calculated_price?: number
	given_price?: number
	comments?: string
	no_of_adults: number
	children: number
	infants: number
	per_person_cost_price?: TFlightPerPersonPrice
	per_person_given_price?: TFlightPerPersonPrice
	cost_per_person_enabled: 0 | 1
	given_per_person_enabled: 0 | 1
}

export type TFlightPaxAllocation = {
	id: string
	label: string
	totalPaxLabel: string
	paxType: TFlightPaxType
	paxCount: number
	ages: number[]
	currency: string
	costPrice: number
	givenPrice: number
}
export interface IFlightSchedule {
	source: TWorldAirport // from departure in auto airport id of source
	destination: TWorldAirport // to arrival
	departs_at: string
	departs_at_local?: string
	arrives_at: string
	arrives_at_local?: string
	airline: TWorldAirline
	airline_id: number
	number_plate: string
	number_plate_formatted: string
	arrival_terminal?: number | string
	departure_terminal?: number | string
	remarks?: string
}
export type TFlightInfoType =
	| "adult_check_in_baggage"
	| "adult_hand_baggage"
	| "infant_hand_baggage"
	| "infant_check_in_baggage"
	| "meal_preference"
	| "fare_type"
	| "category_class"

export type TFlightGroupType =
	| "ROUND_TRIP_ONWARD"
	| "ROUND_TRIP_RETURN"
	| "ONE_WAY"
	| "MULTI_CITY"

export const FLIGHT_TYPE = {
	ONE_WAY: "One Way",
	MULTI_CITY: "Multi City",
	ROUND_TRIP: "Round Trip",
	ROUND_TRIP_ONWARD: "Onward",
	ROUND_TRIP_RETURN: "Return",
}

export interface IFlightService extends IFlightPricing {
	id: number
	groups: Array<{
		type: TFlightGroupType
		schedules: Array<IFlightSchedule>
		info?: Array<{
			id: number
			type: TFlightInfoType
			value: string
		}>
		remarks?: string
	}>
}

export interface IFlightAirPort {
	schedule_date: string
	schedule_date_local: string
	gate: string | null
	terminal?: string | number | null
	airport?: TWorldAirport | null
}

export interface IFlightSchedules {
	schedule: {
		departure: IFlightAirPort
		arrival: IFlightAirPort
		duration: string
		duration_formatted: string
	} | null
	flight: {
		id?: number | string
		number_plate: string
		number_plate_formatted: string
		airline: TWorldAirline
	}
}

export function addLabelToAirport(options: TWorldAirport) {
	const city = options.city?.name || options.iata || ""
	return {
		...options,
		label: `${city ? `${city}: ` : ""} ${options.name}`,
	}
}

/**
 * Passenger age categories:
 * - Infant: age < 2
 * - Child: age 2–12 (inclusive)
 * - Adult: age > 12
 */
export const FLIGHT_INFANT_AGE_LIMIT = 2 // age < 2
export const FLIGHT_CHILD_AGE_LIMIT = 12 // age <= 12 (and >= FLIGHT_INFANT_AGE_LIMIT)

export function getPaxType(age: number): TFlightPaxType {
	if (age < FLIGHT_INFANT_AGE_LIMIT) return "infant"
	if (age <= FLIGHT_CHILD_AGE_LIMIT) return "child"
	return "adult"
}

/**
 * Groups children by pax type with their ages. Returns an object with arrays of ages for each pax type.
 * @param children - An array of children with their ages and counts.
 * @returns An object with arrays of ages for each pax type (infant, child, adult).
 */
export function groupAgesByPaxType(
	children: TChildrenArray
): Record<TFlightPaxType, number[]> {
	const grouped: Record<TFlightPaxType, number[]> = {
		infant: [],
		child: [],
		adult: [],
	}

	for (const child of children) {
		const type = getPaxType(child.age)
		grouped[type].push(...Array(child.count).fill(child.age))
	}

	return grouped
}

export function getChildrenDistribution(
	children: TChildrenArray
): [number, number, number] {
	const { infant, child, adult } = groupAgesByPaxType(children)
	return [infant.length, child.length, adult.length]
}

// to get per person total cp/ sp — sums (per-person price × pax count) for each pax type
export function getFlightPerPersonTotal(
	pricing:
		| IFlightPricing["per_person_cost_price"]
		| IFlightPricing["per_person_given_price"],
	pax: { adults: number; children: number; infants: number },
	currency: string
): number {
	const price = (value?: number) => moneyParseByDecimal(value ?? 0, currency)
	return Number(
		formatMoneyByDecimal(
			addMoney(
				multipleMoneyBy(price(pricing?.adult), pax.adults),
				multipleMoneyBy(price(pricing?.child), pax.children),
				multipleMoneyBy(price(pricing?.infant), pax.infants)
			)
		)
	)
}

export function getProportionalValue(
	perPartValue: number,
	totalPartValue: number,
	totalTargetValue: number,
	currency: string
): number {
	if (totalPartValue <= 0) return 0

	const ratio = getMoneyRatio(
		moneyParseByDecimal(perPartValue, currency),
		moneyParseByDecimal(totalPartValue, currency),
		0
	)

	return Number(
		formatMoneyByDecimal(
			multipleMoneyBy(moneyParseByDecimal(totalTargetValue, currency), ratio)
		)
	)
}

/** Distribute the total flight Cost Price / Given Price into per-pax prices.
Distribution strategy:
	Use weighted distribution when counterpart per-person pricing is available.
	(e.g. derive CP using Given Price weights, or Given Price using CP weights.)
	Otherwise, distribute the total equally across all pax.
**/
export function computePerPersonDistribution(
	totalPrice: number,
	pax: { adults: number; children: number; infants: number },
	options: {
		sourceWeights?: TFlightPerPersonPrice
		currency?: string
	} = {}
): Partial<Record<TFlightPaxType, number>> {
	const { adults, children, infants } = pax
	if (adults + children + infants === 0) return {}
	const { sourceWeights, currency = "INR" } = options

	const paxGroups: {
		type: TFlightPaxType
		count: number
		weight: number
	}[] = [
		{ type: "adult", count: adults, weight: sourceWeights?.adult ?? 0 },
		{ type: "child", count: children, weight: sourceWeights?.child ?? 0 },
		{ type: "infant", count: infants, weight: sourceWeights?.infant ?? 0 },
	]

	const presentPaxGroups = paxGroups.filter((pg) => pg.count > 0)

	// Allocate proportionally by (per-person weight × passenger count).
	// If no weights are available, split equally across all passengers.
	const hasWeights = presentPaxGroups.some((g) => g.weight * g.count > 0)
	const allocationWeights = presentPaxGroups.map((g) =>
		hasWeights ? g.weight * g.count : g.count
	)

	const groupShares = allocateMoneyToRatio(
		moneyParseByDecimal(totalPrice, currency),
		allocationWeights
	)

	const prices: Partial<Record<TFlightPaxType, number>> = {}
	presentPaxGroups.forEach((g, i) => {
		prices[g.type] = Number(
			formatMoneyByDecimal(divideMoneyBy(groupShares[i], g.count))
		)
	})

	return prices
}

function getFlightPaxCounts(pricing: IFlightPricing) {
	const adults = pricing.no_of_adults || 0
	const children = pricing.children || 0
	const infants = pricing.infants || 0

	return {
		adults,
		children,
		infants,
		total: adults + children + infants,
	}
}

export function getFlightNumbers(groups: TFlight["groups"]): string[] {
	return groups
		.flatMap(({ schedules }) => schedules)
		.map(({ airline }) => airline?.number_plate_formatted)
		.filter((number): number is string => Boolean(number))
}

export function getFlightRoutes(groups: TFlight["groups"]) {
	return groups
		.map((group) => {
			const schedules = group.schedules

			if (!schedules.length) return null

			const source = schedules[0]?.source?.iata
			const destinations = schedules
				.map((schedule) => schedule.destination?.iata)
				.filter((iata): iata is string => Boolean(iata))

			if (!source || !destinations.length) return null

			return [source, ...destinations].join(" - ")
		})
		.filter((route): route is string => Boolean(route))
}

function buildFlightLabel(
	flight: TFlight,
	flightType: TFlightType | undefined,
	index: number
) {
	const routes = getFlightRoutes(flight.groups)
	const label = flightType ? `${FLIGHT_TYPE[flightType]} Flight` : "Flight"
	return routes.length
		? `${label} ${routes.map((route) => `(${route})`).join(" • ")}`
		: `${label} ${index + 1}`
}

function normalizePerPersonPrices(
	prices: Partial<Record<TFlightPaxType, number>> | undefined,
	fallback: number
) {
	return {
		adult: Number(prices?.adult ?? fallback),
		child: Number(prices?.child ?? fallback),
		infant: Number(prices?.infant ?? fallback),
	}
}

function resolvePerPersonPricing(
	pricing: IFlightPricing,
	pax: {
		adults: number
		children: number
		infants: number
		total: number
	}
) {
	const currency = pricing.currency
	const totalCostPrice = Number(pricing.calculated_price ?? 0)
	const totalGivenPrice = Number(pricing.given_price ?? 0)
	const defaultCostPrice = Number(
		formatMoneyByDecimal(
			divideMoneyBy(moneyParseByDecimal(totalCostPrice, currency), pax.total)
		)
	)
	const defaultGivenPrice = Number(
		formatMoneyByDecimal(
			divideMoneyBy(moneyParseByDecimal(totalGivenPrice, currency), pax.total)
		)
	)

	const hasPerPersonCP = Boolean(pricing.per_person_cost_price)
	const hasPerPersonSP = Boolean(pricing.per_person_given_price)

	let resolvedCP = normalizePerPersonPrices(undefined, defaultCostPrice)
	let resolvedSP = normalizePerPersonPrices(undefined, defaultGivenPrice)

	if (hasPerPersonCP && hasPerPersonSP) {
		resolvedCP = normalizePerPersonPrices(
			pricing.per_person_cost_price,
			defaultCostPrice
		)
		resolvedSP = normalizePerPersonPrices(
			pricing.per_person_given_price,
			defaultGivenPrice
		)
	} else if (hasPerPersonCP) {
		resolvedCP = normalizePerPersonPrices(
			pricing.per_person_cost_price,
			defaultCostPrice
		)

		// computePerPersonDistribution overrides the resolvedSP with weighted distribution based on resolvedCP
		resolvedSP = {
			...resolvedSP,
			...computePerPersonDistribution(totalGivenPrice, pax, {
				sourceWeights: resolvedCP,
				currency,
			}),
		}
	} else if (hasPerPersonSP) {
		resolvedSP = normalizePerPersonPrices(
			pricing.per_person_given_price,
			defaultGivenPrice
		)
		// computePerPersonDistribution overrides the resolvedCP with weighted distribution based on resolvedSP
		resolvedCP = {
			...resolvedCP,
			...computePerPersonDistribution(totalCostPrice, pax, {
				sourceWeights: resolvedSP,
				currency,
			}),
		}
	} else {
		// Neither per-person CP nor Given Price exists, so distribute total Cost Price / Given Price equally across all passengers.
		resolvedCP = {
			...resolvedCP,
			...computePerPersonDistribution(totalCostPrice, pax, {
				currency,
			}),
		}

		resolvedSP = {
			...resolvedSP,
			...computePerPersonDistribution(totalGivenPrice, pax, {
				currency,
			}),
		}
	}

	return { resolvedCP, resolvedSP }
}
function buildFlightAllocations({
	index,
	label,
	currency,
	pax,
	pricing,
	agesByPaxType,
}: {
	index: number
	label: string
	currency: string
	pax: {
		adults: number
		children: number
		infants: number
	}
	pricing: {
		resolvedCP: Record<TFlightPaxType, number>
		resolvedSP: Record<TFlightPaxType, number>
	}
	agesByPaxType?: Record<TFlightPaxType, number[]>
}): TFlightPaxAllocation[] {
	const paxTypes: { type: TFlightPaxType; count: number }[] = [
		{ type: "adult", count: pax.adults },
		{ type: "child", count: pax.children },
		{ type: "infant", count: pax.infants },
	]

	return paxTypes
		.filter(({ count }) => count > 0)
		.map(({ type, count }) => {
			// only using ages of child and infant according to the pax count in flight
			const ages =
				type === "adult" ? [] : (agesByPaxType?.[type] ?? []).slice(0, count)
			return {
				id: `${index}_flight_${type}`,
				label,
				totalPaxLabel: getFlightTotalPaxLabel(type, count, ages),
				paxType: type,
				paxCount: count,
				ages,
				currency,
				costPrice: pricing.resolvedCP[type],
				givenPrice: pricing.resolvedSP[type],
			}
		})
}

/**
 * Generates the total pax label for a flight allocation by passenger type,
 * Child/infant labels include the passenger ages when available,
 * e.g. `2 Children/Infant (5y, 8y)`.
 */
function getFlightTotalPaxLabel(
	type: TFlightPaxType,
	count: number,
	ages: number[] = []
): string {
	if (type === "adult") {
		return `${count} Pax`
	}

	const label = pluralize(type === "child" ? "Child" : "Infant", count, true)
	const agesLabel = ages
		.filter((a) => a != null)
		.map((age) => `${age}y`)
		.join(", ")

	return agesLabel ? `${label} (${agesLabel})` : label
}

/**
 * Flight Per Person Allocation
 * Pricing resolution:
 * 1. If both per-person CP and Given Price exist, using them directly.
 * 2. If only per-person CP exists, derived Given Price using weighted distribution.
 * 3. If only per-person Given Price exists, derived CP using weighted distribution.
 * 4. If neither exists, distribute total Cost Price / Given Price equally across all pax.
 *
 * Returns one allocation per pax type (adult, child, infant) for
 * each flight.
 */
export function flightPaxAllocation({
	flightService,
	children = [],
	costingCurrency,
	currencyPairs,
}: {
	flightService: IFlightServicesParams
	children?: TChildrenArray
	costingCurrency: string
	currencyPairs: Parameters<typeof makeCurrencyConverter>[0]
}): TFlightPaxAllocation[] {
	const { flights, flight_type: flightType } = flightService
	if (!flights?.length) {
		return []
	}

	const converter = makeCurrencyConverter(currencyPairs, true)
	const toCostingCurrency = (amount: number, fromCurrency: string) =>
		Number(
			formatMoneyByDecimal(
				converter(costingCurrency, moneyParseByDecimal(amount, fromCurrency))
			)
		)

	const agesByPaxType = groupAgesByPaxType(children)

	const getFirstScheduleDepartsAt = (flight: (typeof flights)[number]) => {
		const firstSchedule = flight.groups[0]?.schedules[0]
		if (firstSchedule?.departs_at_local) {
			return convertLocalToUtcAccordingToTimezone(
				firstSchedule.departs_at_local,
				firstSchedule.source?.timezone?.name
			)
		}
		return undefined
	}

	const sortedFlights = flights
		.map((flight) => ({ flight }))
		.sort((a, b) => {
			const aDepartsAt = getFirstScheduleDepartsAt(a.flight)
			const bDepartsAt = getFirstScheduleDepartsAt(b.flight)
			if (!aDepartsAt && !bDepartsAt) return 0
			if (!aDepartsAt) return 1
			if (!bDepartsAt) return -1
			return getDiff(aDepartsAt, bDepartsAt, "milliseconds")
		})

	return sortedFlights.flatMap(({ flight }, sortedIndex) => {
		const pax = getFlightPaxCounts(flight.pricing)

		if (!pax.total || !flight.groups.length) {
			return []
		}

		const label = buildFlightLabel(flight, flightType, sortedIndex)

		const pricing = resolvePerPersonPricing(flight.pricing, pax)
		const allocations = buildFlightAllocations({
			index: sortedIndex,
			label,
			currency: flight.pricing.currency,
			pax,
			pricing,
			agesByPaxType,
		})
		const finalAllocationsInCostingCurrency = allocations.map((allocation) => ({
			...allocation,
			currency: costingCurrency,
			costPrice: toCostingCurrency(allocation.costPrice, allocation.currency),
			givenPrice: toCostingCurrency(allocation.givenPrice, allocation.currency),
		}))
		return finalAllocationsInCostingCurrency
	})
}

export function transformFlightsFormDataToPreviewData(
	flightsFormData: IFlightServicesParams["flights"]
): Array<{ groups: Array<IPreviewFlightData>; pricing: IFlightPricing }> {
	if (!flightsFormData?.length) return []

	return flightsFormData.map((flight) => {
		const flightDetails = (flight.groups || []).map((group) => {
			const scheduleDetails: IPreviewFlightData["flight_details"] =
				group.schedules.map((schedule) => {
					const departs_at = schedule.departs_at_local
						? convertLocalToUtcAccordingToTimezone(
								schedule.departs_at_local,
								schedule.source?.timezone?.name
							)
						: schedule.departs_at
							? convertUtcToLocalAccordingToTimezone(schedule.departs_at)
							: ""
					const arrives_at = schedule.arrives_at_local
						? convertLocalToUtcAccordingToTimezone(
								schedule.arrives_at_local,
								schedule.destination?.timezone?.name
							)
						: schedule.arrives_at
							? convertUtcToLocalAccordingToTimezone(schedule.arrives_at)
							: ""
					const duration = humanizeDuration(getDiff(departs_at, arrives_at))
					return {
						departure: {
							schedule_date: departs_at,
							schedule_date_local: schedule.departs_at_local
								? toISOString(schedule.departs_at_local)
								: "",
							gate: null,
							terminal: schedule.departure_terminal,
							airport: schedule.source || null,
						},
						arrival: {
							schedule_date: arrives_at,
							schedule_date_local: schedule.arrives_at_local
								? toISOString(schedule.arrives_at_local)
								: "",
							gate: null,
							terminal: schedule.arrival_terminal,
							airport: schedule.destination || null,
						},
						duration,
						duration_formatted: duration,
						flight: {
							number_plate: schedule.airline?.number_plate_formatted || "",
							airline: schedule.airline?.airline,
						},
					}
				})

			const from =
				scheduleDetails[0]?.departure?.airport?.city?.name ||
				scheduleDetails[0]?.departure?.airport?.iata ||
				scheduleDetails[0]?.departure?.airport?.name ||
				""

			const to =
				scheduleDetails.at(-1)?.arrival?.airport?.city?.name ||
				scheduleDetails.at(-1)?.arrival?.airport?.iata ||
				scheduleDetails.at(-1)?.arrival?.airport?.name ||
				""

			const via = scheduleDetails
				.slice(1)
				.map((f) => f.departure?.airport?.iata)
				.filter(Boolean) // filter out undefined/empty

			const flight_number = [
				...new Set(scheduleDetails.map((f) => f.flight.number_plate)),
			]
			const additionalOptions = (group.additional_options || []).map(
				(field) => field.value
			)
			return {
				flight_details: scheduleDetails,
				from,
				to,
				type_of_flight: group.type
					? FLIGHT_TYPE[group.type]
					: FLIGHT_TYPE["ONE_WAY"],
				info: {
					adult_check_in_baggage: additionalOptions?.includes("baggage")
						? group.info?.adult_check_in_baggage?.value
						: undefined,
					adult_hand_baggage: additionalOptions?.includes("baggage")
						? group.info?.adult_hand_baggage?.value
						: undefined,
					infant_check_in_baggage:
						additionalOptions?.includes("baggage") && flight.pricing.infants
							? group.info?.infant_check_in_baggage?.value
							: undefined,
					infant_hand_baggage:
						additionalOptions?.includes("baggage") && flight.pricing.infants
							? group.info?.infant_hand_baggage?.value
							: undefined,
					meal_preference: additionalOptions?.includes("meal")
						? group.info?.meal_preference?.value
						: undefined,
					fare_type: additionalOptions?.includes("fare")
						? group.info?.fare_type?.value
						: undefined,
					category_class: group.info?.category_class,
				},
				no_flight:
					scheduleDetails.length > 1
						? `${scheduleDetails.length - 1} Stop`
						: `Non Stop`,
				via: [
					...new Set(
						(via || []).filter((item): item is string => item !== undefined)
					),
				],
				flight_number,
				remarks: additionalOptions?.includes("notes_customer")
					? group.remarks
					: undefined,
			}
		})
		return {
			groups: flightDetails,
			pricing: flight.pricing,
		}
	})
}

export function useFlightPricingConfig({
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
}: {
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
}) {
	// Flight Tax configuration is global across quote options.
	// Therefore tax configuration is always sourced from option[0].
	const { value: gstIncluded } = useFieldValue<boolean>(
		"options[0].gst_included"
	)
	const { value: taxPercentage } = useFieldValue<number | undefined>(
		"options[0].taxes.per_component_allocation.flights_percentage"
	)
	const { value: taxAppliedOn } = useFieldValue<TTaxAppliedOn>(
		"options[0].taxes.per_component_allocation.flights_applied_on"
	)

	// When sync is enabled, all options share option 0's rounding point.
	const { value: enableSyncRoundingAmongOptions } = useFieldValue<
		0 | 1 | undefined
	>("enable_sync_rounding_among_options")

	// In per component startegies flight rounding controled by option 0
	const { value: pricingStrategy } = useFieldValue<
		TGivenQuotePricingStrategy | undefined
	>("pricing_strategy")
	const isPerComponentV2PricingStrategies =
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"
	const optionRoundingPointIndex =
		enableSyncRoundingAmongOptions || isPerComponentV2PricingStrategies
			? 0
			: optionIndex

	const { value: rounding } = useFieldValue<number | undefined>(
		`options[${optionRoundingPointIndex}].margins.rounding_point`
	)

	const { value: costPriceGivenPriceCurrencyPairRatio } =
		useCurrencyPairRatioFieldValue(
			"currency_pairs_obj",
			costPriceCurrency,
			givenPriceCurrency
		)

	const exchangeRate = Number(costPriceGivenPriceCurrencyPairRatio) || 0

	return {
		gstIncluded: Boolean(gstIncluded),
		taxPercentage: Number(taxPercentage || 0),
		taxAppliedOn: (taxAppliedOn || "cost_plus_markup") as TTaxAppliedOn,
		rounding,
		exchangeRate,
	}
}

export function getFlightAllocationResults({
	flightAllocations,
	flightGivenPriceById,
	totalFlightCP,
	totalFlightSP,
	costPriceCurrency,
	givenPriceCurrency,
	taxPercentage,
	taxIncluded,
	taxAppliedOn,
	exchangeRate,
	rounding,
}: {
	flightAllocations: TPricePerPersonAllocation[]
	flightGivenPriceById?: Record<string, number>
	totalFlightCP: number
	totalFlightSP: number
	costPriceCurrency: string
	givenPriceCurrency: string
	taxPercentage: number
	taxIncluded: boolean
	taxAppliedOn: TTaxAppliedOn
	exchangeRate?: number
	rounding?: number
}) {
	return flightAllocations.map((allocation) => {
		const perPaxCP = Number(allocation.price || 0)
		const givenPriceFromAllocation = flightGivenPriceById?.[allocation.id]

		// getProportionalValue is a safety fallback when givenPriceFromAllocation is undefined.
		// Currently, this only happens until flightGivenPriceById is initialized in the form.
		const perPaxSP =
			givenPriceFromAllocation !== undefined
				? givenPriceFromAllocation
				: getProportionalValue(
						perPaxCP,
						totalFlightCP,
						totalFlightSP,
						costPriceCurrency
					)
		return getFlightComponentPriceCalculationValues({
			costPrice: moneyParseByDecimal(perPaxCP, costPriceCurrency),
			givenPriceWithTax: moneyParseByDecimal(perPaxSP, costPriceCurrency),
			taxPercentage,
			taxIncluded,
			taxAppliedOn,
			currency: costPriceCurrency,
			givenCurrency: givenPriceCurrency,
			exchangeRate,
			rounding,
		})
	})
}

export function getFlightAllocationTotals(
	flightAllocations: TPricePerPersonAllocation[],
	allocationResults: ReturnType<typeof getFlightAllocationResults>,
	costPriceCurrency: string,
	givenPriceCurrency: string
) {
	if (!flightAllocations.length || !allocationResults.length) {
		return {
			markup: null,
			markupPercentage: null,
			tax: null,
			unrounded: null,
			rounded: null,
			unroundedInGiven: null,
			roundedInGiven: null,
		}
	}

	const givenCurrency =
		costPriceCurrency === givenPriceCurrency
			? costPriceCurrency
			: givenPriceCurrency || costPriceCurrency

	return flightAllocations.reduce(
		(totals, allocation, i) => {
			const result = allocationResults[i]
			if (!result) return totals
			const pax = allocation.pax_count
			return {
				markup: addMoney(totals.markup, multipleMoneyBy(result.markup, pax)),
				markupPercentage: result.markupPercentage,
				tax: addMoney(totals.tax, multipleMoneyBy(result.tax, pax)),
				unrounded: addMoney(
					totals.unrounded,
					multipleMoneyBy(result.unrounded, pax)
				),
				rounded: addMoney(totals.rounded, multipleMoneyBy(result.rounded, pax)),
				unroundedInGiven: addMoney(
					totals.unroundedInGiven,
					multipleMoneyBy(result.unroundedInGiven, pax)
				),
				roundedInGiven: addMoney(
					totals.roundedInGiven,
					multipleMoneyBy(result.roundedInGiven, pax)
				),
			}
		},
		{
			markup: createMoney(0, costPriceCurrency),
			tax: createMoney(0, costPriceCurrency),
			unrounded: createMoney(0, costPriceCurrency),
			rounded: createMoney(0, costPriceCurrency),
			unroundedInGiven: createMoney(0, givenCurrency),
			roundedInGiven: createMoney(0, givenCurrency),
		}
	)
}

/**
 * Computes pricing calculations for every flight pax allocation.
 *
 * For each allocation this hook resolves:
 * - selling price
 * - markup
 * - tax
 * - currency conversion
 * - optional rounding
 *
 * It also returns the aggregated rounded selling price across all flight
 * allocations for use in pricing bifurcation.
 */
export function useFlightAllocationsPricing({
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
	flightCostPriceInCostingCurrency,
	flightPriceInCostingCurrency,
	flightAllocations,
	enableFlightRounding,
}: {
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
	flightCostPriceInCostingCurrency: number
	flightPriceInCostingCurrency: number
	flightAllocations: TPricePerPersonAllocation[]
	enableFlightRounding: boolean
}) {
	const { gstIncluded, taxPercentage, taxAppliedOn, rounding, exchangeRate } =
		useFlightPricingConfig({
			optionIndex,
			costPriceCurrency,
			givenPriceCurrency,
		})

	const { value: flightGivenPriceById } = useFieldValue<
		Record<string, number> | undefined
	>(`options[${optionIndex}].flight_given_prices_by_id`)

	const allocationResults = useMemo(() => {
		return getFlightAllocationResults({
			flightAllocations,
			flightGivenPriceById,
			totalFlightCP: flightCostPriceInCostingCurrency,
			totalFlightSP: flightPriceInCostingCurrency,
			costPriceCurrency,
			givenPriceCurrency,
			taxPercentage,
			taxIncluded: gstIncluded,
			taxAppliedOn,
			exchangeRate,
			rounding: enableFlightRounding ? rounding : undefined,
		})
	}, [
		flightAllocations,
		flightGivenPriceById,
		flightCostPriceInCostingCurrency,
		flightPriceInCostingCurrency,
		taxPercentage,
		gstIncluded,
		taxAppliedOn,
		costPriceCurrency,
		givenPriceCurrency,
		exchangeRate,
		rounding,
		enableFlightRounding,
	])

	// Sum every money amount across the per-person flight allocations
	// (each allocation's amount x its pax count). Cost-currency amounts total
	// in costPriceCurrency; the *InGiven amounts total in the selling currency.
	// Returns null for each total when there are no allocations.
	const flightAllocationTotals = useMemo(() => {
		return getFlightAllocationTotals(
			flightAllocations,
			allocationResults,
			costPriceCurrency,
			givenPriceCurrency
		)
	}, [
		flightAllocations,
		allocationResults,
		costPriceCurrency,
		givenPriceCurrency,
	])

	return { allocationResults, flightAllocationTotals }
}
